import { Alert, Badge, DefinitionList, Drawer, EmptyState, Link, Stack, t, Text } from '@kanbios/ui';
import type { Container } from './RunningPanel';
import type { Cve, Technology } from '../lib/technologies';
import { behindState, cveLink, severityTone, vulnerabilityTotal } from '../lib/technologies';
import { relativeTime, shortDate, shortDateTime } from '../lib/format';
import { ListTable } from './ListTable';
import { runningList } from './RunningPanel';

const SEVERITIES = ['critical', 'high', 'medium', 'low', 'unknown'] as const;

type Row = Technology & { key: string; kind: 'runtime' | 'service' | 'framework' };

/**
 * D51: a container's technologies in detail (D46), from the application page's
 * Briques tab: its runtimes, services (D60) and frameworks with version, endoflife.date cycle
 * and end of support, its image's known vulnerabilities by severity, when
 * Trivy analysed it. D56: under the counts, the list of the image's known
 * vulnerabilities, critical first (SPEC-018's columns); the ranking by real
 * risk, deadlines and fixes stay in lot 3. D61: each technology's latest
 * release (a framework's from its registry, a runtime's or a service's the
 * latest patch of its cycle) and, in its own column (D58), how far behind it is.
 */
export function TechnologiesDrawer({ server, container, onClose }: { server: string | null; container: Container | null; onClose: () => void }) {
  const x = container?.technologies ?? null;
  const support = (r: Row) => {
    if (r.ended) {
      return (
        // D47, D60: a runtime or a service past its end of life is a defect of the application.
        <Badge tone={r.kind === 'framework' ? 'warn' : 'neg'} size="sm">
          {r.endOfLife ? t('technologies.endedSince', { date: shortDate(r.endOfLife) }) : t('running.technologies.endOfLife')}
        </Badge>
      );
    }
    return <Text variant="body-sm" tone={r.endOfLife ? undefined : 'mute'} truncate>{r.endOfLife ? t('technologies.until', { date: shortDate(r.endOfLife) }) : t('technologies.unknown')}</Text>;
  };
  const behind = (r: Row) => {
    const state = behindState(r);
    if (!state) return <Text variant="body-sm" tone="mute">—</Text>;
    const help = [
      t(r.kind === 'framework' ? 'technologies.behindHelp' : 'technologies.behindHelpCycle', { version: r.version ?? '', latest: r.latest ?? '', cycle: r.cycle ?? '' }),
      r.behind && r.version?.startsWith('0.') ? t('technologies.behindHelpZero') : '',
    ].filter(Boolean).join(' ');
    return <Badge tone={state.tone} size="sm" title={help}>{t(state.label, { n: r.behind?.count ?? 0 })}</Badge>;
  };
  const v = x?.vulnerabilities ?? null;
  return (
    <Drawer
      open={Boolean(container)}
      onClose={onClose}
      size="xl"
      eyebrow={t('technologies.eyebrow')}
      title={container?.name ?? ''}
      subtitle={container ? <Text variant="code" truncate>{container.image}</Text> : undefined}
    >
      {container && (
        !x ? <EmptyState framed={false} icon="hourglass_top" title={t('technologies.notRead')}>{t('technologies.notReadBody')}</EmptyState> : (
          <Stack gap={24}>
            {/* D55: analysed again after « Relancer l'analyse »: the previous results stay until the new ones. */}
            {x.status === 'pending' && (x.analysedAt
              ? <Alert tone="info" title={t('technologies.reanalysing')}>{t('technologies.reanalysingBody')}</Alert>
              : <Alert tone="info" title={t('running.technologies.pending')}>{t('technologies.pendingBody')}</Alert>)}
            {x.status === 'failed' && (
              <Alert tone="warn" title={t('technologies.failed')}>
                {t(x.error === 'database' ? 'running.technologies.error.database' : 'running.technologies.error.scan')}
              </Alert>
            )}
            {/* D120: searched, filtered and sorted by the server, as every list. */}
            <ListTable<Row>
              label={t('technologies.table', { container: container.name })}
              queryKey={['server-running-list', server, 'technologies', container.name]}
              fetch={runningList<Row>(server ?? '', 'technologies')}
              fixed={{ container: container.name }}
              rowKey="key"
              defaultSort={{ key: 'kind', dir: 'asc' }}
              search={t('technologies.search')}
              facets={[
                { key: 'kind', label: t('technologies.columns.kind'), valueLabel: v => t(`technologies.kind.${v}`) },
                { key: 'ended', label: t('technologies.columns.endOfLife'), valueLabel: v => t(v === 'yes' ? 'running.technologies.endOfLife' : 'technologies.supported') },
              ]}
              refetchInterval={false}
              columns={[
                { key: 'label', label: t('technologies.columns.name'), strong: true, pinned: true, grow: true, width: 160 },
                { key: 'kind', label: t('technologies.columns.kind'), width: 120, accessor: (r: Row) => t(`technologies.kind.${r.kind}`) },
                { key: 'version', label: t('technologies.columns.version'), mono: true, width: 110, sortable: false, accessor: (r: Row) => r.version ?? '—' },
                // D61: the latest release, and how far behind it the version is, each in its own column (D58).
                { key: 'latest', label: t('technologies.columns.latest'), description: t('technologies.columns.latestHelp'), mono: true, width: 130, sortable: false, accessor: (r: Row) => r.latest ?? '—' },
                { key: 'behind', label: t('technologies.columns.behind'), description: t('technologies.columns.behindHelp'), width: 210,
                  accessor: (r: Row) => { const state = behindState(r); return state ? t(state.label, { n: r.behind?.count ?? 0 }) : ''; },
                  render: behind },
                { key: 'cycle', label: t('technologies.columns.cycle'), mono: true, width: 90, sortable: false, accessor: (r: Row) => r.cycle ?? '—' },
                { key: 'endOfLife', label: t('technologies.columns.endOfLife'), width: 220, render: support },
              ]}
              empty={<EmptyState framed={false} icon="deployed_code" title={t('running.technologies.none')} />}
            />
            <Stack gap={8}>
              <Text variant="card-title">{t('technologies.vulnerabilities')}</Text>
              {v ? (
                <DefinitionList
                  columns={2}
                  items={[
                    ...SEVERITIES.map(s => ({ label: t(`technologies.severity.${s}`), value: String(v[s]) })),
                    { label: t('technologies.total'), value: String(vulnerabilityTotal(v)) },
                  ]}
                />
              ) : <Text variant="body-sm" tone="mute">{t('running.technologies.notAnalysed')}</Text>}
              <DefinitionList items={[{ label: t('technologies.analysedAt'), value: x.analysedAt ? `${shortDateTime(x.analysedAt)} · ${relativeTime(x.analysedAt)}` : null }]} />
            </Stack>
            {/* D56: the list, as the agent sends it (critical first, at most 100); null before running v11. */}
            {v && (
              <Stack gap={8}>
                <Text variant="card-title">{t('technologies.cves.title')}</Text>
                {x.cves ? (
                  <ListTable<Cve & { key: string }>
                    label={t('technologies.cves.table', { container: container.name })}
                    queryKey={['server-running-list', server, 'cves', container.name]}
                    fetch={runningList<Cve & { key: string }>(server ?? '', 'cves')}
                    fixed={{ container: container.name }}
                    rowKey="key"
                    defaultSort={{ key: 'severity', dir: 'asc' }}
                    search={t('technologies.cves.search')}
                    facets={[
                      { key: 'severity', label: t('technologies.cves.columns.severity'), valueLabel: v => t(`technologies.cves.severity.${v}`) },
                      { key: 'fixed', label: t('technologies.cves.columns.fixed'), valueLabel: v => t(v === 'yes' ? 'technologies.cves.fixable' : 'technologies.cves.unfixable') },
                    ]}
                    refetchInterval={false}
                    columns={[
                      { key: 'severity', label: t('technologies.cves.columns.severity'), width: 110, pinned: true,
                        accessor: (c: Cve) => t(`technologies.cves.severity.${c.severity}`),
                        render: (c: Cve) => <Badge tone={severityTone(c.severity)} size="sm">{t(`technologies.cves.severity.${c.severity}`)}</Badge> },
                      { key: 'id', label: t('technologies.cves.columns.id'), width: 170, render: (c: Cve) => {
                        const href = cveLink(c.id);
                        return href ? <Link href={href} external><Text variant="code">{c.id}</Text></Link> : <Text variant="code" truncate>{c.id}</Text>;
                      } },
                      { key: 'package', label: t('technologies.cves.columns.package'), mono: true, width: 160 },
                      { key: 'installed', label: t('technologies.cves.columns.installed'), mono: true, width: 140, sortable: false, accessor: (c: Cve) => c.installed ?? '—' },
                      { key: 'fixed', label: t('technologies.cves.columns.fixed'), mono: true, width: 140, sortable: false, accessor: (c: Cve) => c.fixed ?? '—' },
                      { key: 'title', label: t('technologies.cves.columns.title'), grow: true, width: 260, sortable: false, accessor: (c: Cve) => c.title ?? '—' },
                    ]}
                    empty={<EmptyState framed={false} icon="verified" title={t('technologies.cves.empty')} />}
                  />
                ) : <Text variant="body-sm" tone="mute">{t('technologies.cves.notRead')}</Text>}
                {x.cves && x.cvesTruncated > 0 && <Text variant="body-sm" tone="mute">{t('technologies.cves.more', { n: x.cvesTruncated })}</Text>}
              </Stack>
            )}
            <Text variant="body-sm" tone="mute">{t('technologies.lot3')}</Text>
          </Stack>
        )
      )}
    </Drawer>
  );
}
